import React, { useState, useEffect } from "react";
import {
  Upload,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Menu,
  X,
  RefreshCw,
  Download,
  FolderOpen,
  Database,
  Zap,
} from "lucide-react";
import FileUpload from "../FileUpload/FileUpload";
import { SAMPLE_MODELS } from "../../constants/sampleModels";
import TokenService from "../../services/TokenService";
import "./Sidebar.css";
import { getFileUrn, deleteFileUrn } from "./api/index";
const Sidebar = ({
  documentUrn,
  setDocumentUrn,
  accessToken,
  loading,
  onLoadModel,
  onLoadSampleModel,
  onFileUploaded,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [getFiles, setGetFile] = useState([]);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // ՈՒՂՂՈՒՄ 1: localStorage-ի ճիշտ հանդլինգ
  useEffect(() => {
    const savedFiles = localStorage.getItem("uploadedFiles");
    if (savedFiles) {
      try {
        const parsedFiles = JSON.parse(savedFiles);
        const filesWithDates = parsedFiles.map((file) => ({
          ...file,
          uploadedAt: new Date(file.uploadedAt),
        }));
        setUploadedFiles(filesWithDates);
      } catch (error) {
        console.error("Error loading saved files:", error);
        localStorage.removeItem("uploadedFiles");
      }
    }
  }, []);
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getFileUrn();

        // setApplications(res.users || []);

        setGetFile(res);
      } catch (error) {
        console.error("Failed to load users:", error);
      }
    };

    fetchUsers();
  }, []);
  // useEffect(() => {
  //   if (uploadedFiles.length > 0) {
  //     localStorage.setItem("uploadedFiles", JSON.stringify(uploadedFiles));
  //   }
  // }, [uploadedFiles]);

  useEffect(() => {
    if (documentUrn && uploadedFiles.length > 0) {
      onLoadModel(documentUrn);
    }
  }, [accessToken, loading, documentUrn, uploadedFiles]);

  // ՈՒՂՂՈՒՄ 2: Մոդելի բեռնման լոգիկան
  // useEffect(() => {
  //   console.log("documentUrn changed:", documentUrn);
  //   if (documentUrn && onLoadModel) {
  //     onLoadModel(documentUrn);
  //   }
  // }, [documentUrn, onLoadModel]);
  useEffect(() => {
    if (documentUrn && uploadedFiles.length > 0) {
      onLoadModel(documentUrn);
    }
  }, [accessToken, loading, documentUrn, uploadedFiles]);

  // ՈՒՂՂՈՒՄ 3: Sample model-ի ավտոմատ բեռնումը հանել
  useEffect(() => {
    if (accessToken && !loading && SAMPLE_MODELS.length > 0) {
      onLoadSampleModel(SAMPLE_MODELS[0]);
    }
  }, [accessToken, loading]);

  useEffect(() => {
    updateTokenInfo();
  }, [accessToken]);

  useEffect(() => {
    refreshToken();
    const interval = setInterval(
      () => {
        refreshToken();
      },
      30 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, []);

  const updateTokenInfo = () => {
    if (accessToken) {
      const expiry = TokenService.getTokenExpiry();
      if (expiry) {
        setTokenInfo({
          expiresAt: expiry,
          timeRemaining: Math.floor(
            (expiry.getTime() - Date.now()) / 1000 / 60,
          ),
        });
      }
    }
  };

  const refreshToken = async () => {
    try {
      setIsRefreshingToken(true);
      TokenService.clearToken();
      const tokenData = await TokenService.getAccessToken();
      TokenService.storeToken(tokenData);
      updateTokenInfo();
    } catch (error) {
      console.error("Token refresh failed:", error);
    } finally {
      setIsRefreshingToken(false);
    }
  };

  const handleFileUploaded = (result) => {
    const fileInfo = {
      id: Date.now(),
      name: result.fileName,
      urn: result.urn,
      uploadedAt: new Date(),
      status: "success",
      jobId: result.jobId,
      // progress: "0%",
    };

    setUploadedFiles((prev) => [fileInfo, ...prev]);
    onFileUploaded?.(result);
    pollTranslationStatus(result.urn, result.jobId);
  };

  const pollTranslationStatus = async (urn, jobId) => {
    try {
      const FileUploadService = (
        await import("../../services/FileUploadService")
      ).default;

      const checkStatus = async () => {
        try {
          // const manifest = await FileUploadService.getTranslationStatus(
          //   urn,
          //   accessToken
          // );
          const manifest = "success";

          setUploadedFiles((prev) =>
            prev.map((file) =>
              file.urn === urn
                ? {
                    ...file,
                    status: manifest.status,
                    progress: manifest.progress || "0%",
                  }
                : file,
            ),
          );

          if (manifest.status === "success") {
            return;
          } else if (manifest.status === "failed") {
            console.error("Translation failed for:", urn);
            return;
          } else {
            setTimeout(checkStatus, 5000);
          }
        } catch (error) {
          console.error("Status check failed:", error);
          setTimeout(checkStatus, 5000);
        }
      };

      setTimeout(checkStatus, 10000);
    } catch (error) {
      console.error("Failed to start status polling:", error);
    }
  };

  const handleUploadError = (error) => {
    console.error("Upload error:", error);
  };

  // ՈՒՂՂՈՒՄ 4: loadUploadedModel ֆունկցիան
  const loadUploadedModel = (file) => {
    // console.log("Loading uploaded model:", file);
    file.status = "success";
    if (file.status === "success") {
      setDocumentUrn(file.urn);
      // Անմիջապես փոխել active tab-ը viewer-ին ցույց տալու համար
      if (window.location.pathname.includes("/")) {
        // Navigate to viewer if not already there
      }
    } else {
      console.warn("File not ready:", file.status);
      alert(`The file is not ready yet: Status: ${getStatusText(file.status)}`);
    }
  };

  const deleteUploadedFile = (fileId, event) => {
    event.stopPropagation();
    setUploadedFiles((prev) => {
      const newFiles = prev.filter((file) => file.id !== fileId);
      if (newFiles.length === 0) {
        localStorage.removeItem("uploadedFiles");
      }
      return newFiles;
    });
  };

  const clearAllFiles = async () => {
    await deleteFileUrn();
    setGetFile([]);
    // console.log("Clearing all files");
    // setUploadedFiles([]);
    // localStorage.removeItem("uploadedFiles");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "processing":
      case "inprogress":
        return <Clock className="w-4 h-4 text-yellow-500 animate-spin" />;
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
      case "error":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "processing":
      case "inprogress":
        return "Processing";
      case "success":
        return "Ready";
      case "failed":
        return "Failed";
      case "error":
        return "Error";
      default:
        return "Unknown";
    }
  };

  const formatTimeAgo = (date) => {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} a minute ago`;
    if (hours < 24) return `${hours} an hour ago`;
    return date.toLocaleDateString("hy-AM");
  };

  // ՈՒՂՂՈՒՄ 5: Sample model հանդլեր
  const handleSampleModelClick = (model) => {
    onLoadSampleModel(model);
  };

  const tabs = [
    { id: "upload", name: "upload", icon: <Upload className="w-4 h-4" /> },
    { id: "files", name: "files", icon: <FolderOpen className="w-4 h-4" /> },
    {
      id: "samples",
      name: "Examples",
      icon: <Database className="w-4 h-4" />,
    },
  ];

  // ՈՒՂՂՈՒՄ 6: Debug ինֆորմացիա
  // console.log("Sidebar state:", {
  //   documentUrn,
  //   accessToken: accessToken,
  //   loading,
  //   uploadedFiles: uploadedFiles.length,
  //   activeTab,
  // });

  return (
    <>
      {/* Overlay for mobile */}
      {!isCollapsed && (
        <div className="sidebar-overlay" onClick={() => setIsCollapsed(true)} />
      )}

      <aside
        className={`modern-sidebar ${isCollapsed ? "collapsed" : "expanded"}`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-branding">
            {!isCollapsed && (
              <>
                {/* <div className="brand-icon">
                  <Zap className="w-6 h-6" />
                </div> */}
                <h2>ViewStruct</h2>
              </>
            )}
          </div>

          <button
            className="toggle-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Open the panel" : "Close the panel"}
          >
            {isCollapsed ? (
              <Menu className="w-5 h-5" />
            ) : (
              <X className="w-5 h-5" />
            )}
          </button>
        </div>

        {!isCollapsed && (
          <>
            {/* Debug Info - հանել production-ում */}
            {/* <div
              style={{
                padding: "10px",
                background: "#f0f0f0",
                fontSize: "12px",
              }}
            >
              <div>URN: {documentUrn || "None"}</div>
              <div>Token: {accessToken ? "Yes" : "No"}</div>
              <div>Files: {uploadedFiles.length}</div>
            </div> */}

            {/* Token Status */}
            {/* {accessToken && (
              <div className="token-status-card">
                <div className="token-status-header">
                  <span className="status-indicator online"></span>
                  <span className="status-text">Միացված</span>
                  <button
                    onClick={refreshToken}
                    className="refresh-token-btn"
                    disabled={isRefreshingToken}
                    title="Թարմացնել token-ը"
                  >
                    <RefreshCw
                      className={`w-3 h-3 ${
                        isRefreshingToken ? "animate-spin" : ""
                      }`}
                    />
                  </button>
                </div>
                {tokenInfo && (
                  <div className="token-info">
                    <small>
                      Մնացած ժամանակ: {tokenInfo.timeRemaining} րոպե
                    </small>
                  </div>
                )}
              </div>
            )} */}

            {/* Navigation Tabs */}
            <div className="sidebar-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
                >
                  {tab.icon}
                  <span>{tab.name}</span>
                  {tab.id === "files" && getFiles.length > 0 && (
                    <span className="badge">{getFiles.length}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="sidebar-content">
              {/* Upload Tab */}
              {activeTab === "upload" && (
                <div className="tab-content">
                  <div className="content-header">
                    <h3>Load New Model</h3>
                    <p>Select a CAD file from your system</p>
                  </div>

                  {accessToken ? (
                    <div className="upload-section">
                      <FileUpload
                        onFileUploaded={handleFileUploaded}
                        onUploadProgress={setUploadProgress}
                        onUploadError={handleUploadError}
                        accessToken={accessToken}
                        disabled={loading}
                      />

                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="upload-progress">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <span className="progress-text">
                            {uploadProgress}%
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-access">
                      <p>Token is loading...</p>
                    </div>
                  )}
                </div>
              )}

              {/* Files Tab */}
              {activeTab === "files" && (
                <div className="tab-content">
                  <div className="content-header">
                    <h3>Your Models</h3>
                    {getFiles.length > 0 && (
                      <button onClick={clearAllFiles} className="clear-all-btn">
                        Clear All
                      </button>
                    )}
                  </div>

                  {getFiles.length === 0 ? (
                    <div className="empty-state">
                      <Upload className="w-12 h-12 text-gray-300" />
                      <p>You haven’t uploaded any files yet</p>
                      <button
                        onClick={() => setActiveTab("upload")}
                        className="empty-state-btn"
                      >
                        Upload the first file
                      </button>
                    </div>
                  ) : (
                    <div className="files-grid">
                      {getFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`file-card ${file.status} ${
                            file.status === "success" ? "clickable" : ""
                          }`}
                          onClick={() => loadUploadedModel(file)}
                          style={{
                            cursor:
                              file.status === "success" ? "pointer" : "default",
                          }}
                        >
                          <div className="file-card-header">
                            {getStatusIcon(file.status)}
                            <button
                              onClick={(e) => deleteUploadedFile(file.id, e)}
                              className="delete-file-btn"
                              title="Ջնջել"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>

                          <div className="file-card-content">
                            <h4 className="file-name" title={file.description}>
                              {file.description}
                            </h4>
                            <div className="file-meta">
                              <span className="file-status">
                                {getStatusText(file.status)}
                              </span>
                              {file.status === "processing" &&
                                file.progress && (
                                  <span className="file-progress">
                                    {file.progress}
                                  </span>
                                )}
                            </div>
                            <div className="file-time">
                              {formatTimeAgo(file.updated_at)}
                            </div>
                          </div>

                          {file.status === "success" && (
                            <div className="file-card-footer">
                              <span className="ready-indicator">
                                Ready to view
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Samples Tab */}
              {activeTab === "samples" && (
                <div className="tab-content">
                  <div className="content-header">
                    <h3>Sample Models</h3>
                    <p>Try our material samples</p>
                  </div>

                  <div className="samples-grid">
                    {SAMPLE_MODELS.map((model, index) => (
                      <button
                        key={index}
                        className="sample-card"
                        onClick={() => handleSampleModelClick(model)}
                        disabled={!accessToken || loading}
                      >
                        <div className="sample-icon">
                          <FileText className="w-8 h-8" />
                        </div>
                        <div className="sample-content">
                          <h4 className="sample-name">{model.name}</h4>
                          <p className="sample-desc">{model.description}</p>
                        </div>
                        <div className="sample-arrow">
                          <Download className="w-4 h-4" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Collapsed State */}
        {isCollapsed && (
          <div className="collapsed-content">
            <div className="collapsed-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsCollapsed(false);
                  }}
                  className={`collapsed-tab ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  title={tab.name}
                >
                  {tab.icon}
                  {tab.id === "files" && uploadedFiles.length > 0 && (
                    <span className="collapsed-badge">
                      {uploadedFiles.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
